import React, { useEffect, useMemo, useState } from "react";
import { matchPath, Redirect, Route, Switch, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import GridViewIcon from "@mui/icons-material/GridView";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SyncIcon from "@mui/icons-material/Sync";
import ViewListIcon from "@mui/icons-material/ViewList";
import { Button, Stack, StackProps, styled } from "@mui/material";
import { ProgressOverlay } from "components";
import { StatusLabel } from "components/data-rooms";
import { DataroomCompact } from "components/data-rooms/Compact";
import { RoomReconnect } from "components/modals";
import { Menu } from "components/popups";
import { Routes } from "constants/routes";
import { useActionHook, useModals, useNavigate, usePathParam, useQueryHook } from "hooks";
import { DataroomFetchers, DataroomFiles } from "pages";
import { useConnectionSelect, useDataRoomsActions } from "store";

import "./DataRoom.styles.scss";

const tabGreenBar: StackProps["sx"] = {
  ":after": {
    // eslint-disable-next-line quotes
    content: '""',
    position: "absolute",
    bottom: "-5px",
    height: "4px",
    width: "100%",
    backgroundColor: "#26a6ae",
    borderRadius: "2px"
  }
};

export const DataRoom: React.FC = () => {
  const [isReconnect, setIsReconnect] = useState<number | null>(null);

  const connection = useConnectionSelect();

  const { clearConnectionAction, deleteConnection, importFilesForDataroomAction, getConnectionAction } =
    useDataRoomsActions();
  const dataRoomUrlId = usePathParam("data_rooms");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { successModal, infoModal } = useModals();

  const { action: importDataRoomAction } = useActionHook(importFilesForDataroomAction, {
    onSuccess: (msg) => {
      successModal({ msg });
    }
  });

  const checkFilesTabAvailable = connection?.relations.find((item) => item.consumer !== null);
  const isConnections = Boolean(matchPath(pathname, Routes.dataRoomConnections));
  const isFiles = Boolean(matchPath(pathname, Routes.dataRoomFiles));

  const { loading, refetch, error } = useQueryHook(() => getConnectionAction(dataRoomUrlId!));
  useEffect(() => {
    return () => {
      toast.dismiss();
      clearConnectionAction();
    };
  }, []);

  const StyledTabSwitcher = styled(Stack)(() => ({
    flexDirection: "row",
    alignItems: "center",
    gap: "5px",
    fontSize: "16px",
    cursor: "pointer",
    position: "relative"
  }));

  const roomData = useMemo(() => {
    return {
      last_connected: connection?.last_connected!,
      logo: connection?.logo!,
      subdomain: connection?.subdomain!,
      type: connection?.type!,
      username: connection?.username!,
      isLoginValid: connection?.is_login_valid!
    };
  }, [connection]);

  return (
    <Stack
      className="dataroom-page"
      sx={{
        alignItems: "stretch",
        flexGrow: 1,
        gap: "20px",
        overflow: "hidden",
        "& .user-profile-popup": {
          minHeight: "320px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }
      }}
    >
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Stack direction="row" sx={{ gap: "18px" }}>
          <StyledTabSwitcher
            {...(isConnections && { sx: tabGreenBar })}
            onClick={() => {
              if (!dataRoomUrlId) return;
              if (loading) return;
              navigate({ route: "dataRoomConnections", params: { room_id: dataRoomUrlId } });
            }}
          >
            <GridViewIcon fontSize="md" />
            Deals Connection
          </StyledTabSwitcher>
          <StyledTabSwitcher
            sx={{
              ...(!checkFilesTabAvailable && { opacity: "0.6" }),
              ...(isFiles && { ...tabGreenBar })
            }}
            onClick={() => {
              if (!checkFilesTabAvailable) return;
              if (!dataRoomUrlId) return;
              if (loading) return;
              navigate({ route: "dataRoomFiles", params: { room_id: dataRoomUrlId } });
            }}
          >
            <ViewListIcon fontSize="md" />
            Files
          </StyledTabSwitcher>
        </Stack>

        <Stack
          className="dataroom-controls-section"
          direction="row"
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "14px",
            minHeight: "80px",
            "& .user-profile-popup": {
              minHeight: "320px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between"
            }
          }}
        >
          <DataroomCompact
            logo={roomData.logo}
            type={roomData.type}
            username={roomData.username}
            isLoginValid={roomData.isLoginValid}
            onReconnect={() => dataRoomUrlId && setIsReconnect(dataRoomUrlId)}
          />
          {/* <Typography variant="body2" sx={{ fontSize: "14px" }}>
            {connection?.type + " / "} {connection?.subdomain ? connection?.subdomain + " / " : ""}{" "}
            {connection?.username}
          </Typography> */}
          {(loading || error) && (
            <StatusLabel
              state={
                loading
                  ? "import"
                  : error && error.message?.includes("The email and password you entered did not match our records")
                    ? "update"
                    : "error"
              }
              onClick={() => {
                if (!dataRoomUrlId) return;
                setIsReconnect(dataRoomUrlId);
              }}
            />
          )}
          <Button
            size="sm"
            variant="outlined"
            disabled={!connection?.relations?.length}
            startIcon={<SyncIcon sx={{ fontSize: "20px" }} />}
            onClick={() => {
              if (connection?.is_login_valid) {
                connection?.id && importDataRoomAction(connection.id);
              } else {
                infoModal({ msg: "Please reconnect to Data Room to import files." });
              }
            }}
          >
            Index Files & Sync
          </Button>
          {/* <IconButton onClick={refetch} sx={iconBtnsStyles} size="sm">
            <RefreshIcon fontSize="md" />
          </IconButton> */}
          <Menu
            iconButtonProps={{ sx: iconBtnsStyles, size: "sm" }}
            icon={<MoreVertIcon fontSize="md" />}
            options={[
              {
                title: "Disconnect Data Room",
                icon: <ExitToAppIcon sx={{ fontSize: "20px" }} />,
                onClick: async () => {
                  const id = connection?.id ?? dataRoomUrlId;
                  if (!id) return;
                  await deleteConnection(id);
                  navigate({ route: "dataRooms" });
                }
              }
            ]}
          />
        </Stack>
      </Stack>
      <ProgressOverlay
        inProgress={loading}
        spinnerProps={{ size: 150 }}
        containerSX={{ display: "flex", flexGrow: 1, flexDirection: "column", overflow: "hidden" }}
      >
        <Switch>
          <Redirect exact from={Routes.dataRoom} to={Routes.dataRoomConnections} />
          <Route
            exact
            path={Routes.dataRoomConnections}
            component={() => <DataroomFetchers dataRoomUrlId={dataRoomUrlId} />}
          />
          <Route
            exact
            path={Routes.dataRoomFiles}
            render={(props) => <DataroomFiles {...props} dataroomId={dataRoomUrlId} />}
          />
        </Switch>
      </ProgressOverlay>
      <RoomReconnect
        connectParams={null}
        onSkip={() => {}}
        showSkip={false}
        onCancel={() => setIsReconnect(null)}
        onClose={() => {
          refetch();
          setIsReconnect(null);
        }}
        connectionId={isReconnect}
      />
    </Stack>
  );
};

const iconBtnsStyles = {
  color: "#0a3142",
  background: "white",
  border: "0.5px solid #bac7ce"
};